// MobileFAB — Floating action button for quick opportunity execution.
// Gold pulse when BET_NOW opportunities exist; brand blue for QUALIFIED only.
// Hidden on md+ (desktop uses the full opportunity stream instead).

import { motion, AnimatePresence } from 'framer-motion';
import { Zap } from 'lucide-react';
import clsx from 'clsx';
import { useAppStore } from '../../store/useAppStore';

interface MobileFABProps {
  onOpen: () => void;
}

export function MobileFAB({ onOpen }: MobileFABProps) {
  const recommendations = useAppStore((s) => s.recommendations);

  const betNowCount = recommendations.filter(
    (r) => r.decisionStatus === 'STRONG_BET' && r.timingRecommendation === 'BET_NOW',
  ).length;

  const qualifiedCount = recommendations.filter(
    (r) => r.decisionStatus === 'QUALIFIED_BET' || r.decisionStatus === 'STRONG_BET',
  ).length;

  const totalCount = qualifiedCount;
  const isBetNow   = betNowCount > 0;

  if (totalCount === 0) return null;

  return (
    <AnimatePresence>
      <motion.button
        key="mobile-fab"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 400, damping: 28 }}
        whileTap={{ scale: 0.92 }}
        onClick={onOpen}
        className={clsx(
          'fixed bottom-20 right-4 z-[45] md:hidden',
          'w-14 h-14 rounded-full flex items-center justify-center',
          'shadow-lg active:shadow-md transition-shadow duration-200',
          isBetNow
            ? 'bg-gold text-surface-0 shadow-gold/30'
            : 'bg-brand text-white shadow-brand/30',
        )}
        aria-label={`${totalCount} opportunities — tap to view`}
      >
        {/* Pulse ring — only for BET_NOW urgency */}
        {isBetNow && (
          <span className="absolute inset-0 rounded-full animate-ping bg-gold opacity-30" />
        )}

        <Zap className="w-6 h-6 relative z-10" fill="currentColor" />

        {/* Count badge */}
        <span
          className={clsx(
            'absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full',
            'flex items-center justify-center text-[10px] font-bold leading-none',
            isBetNow
              ? 'bg-surface-0 text-gold'
              : 'bg-win text-surface-0',
          )}
        >
          {totalCount > 9 ? '9+' : totalCount}
        </span>
      </motion.button>
    </AnimatePresence>
  );
}
